import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Toggle } from '../ui/Toggle';
import { Search, X, ImageOff, Star, Plus, Sparkles, Link2, ChevronDown, ChevronUp, Layers, Trash2, Zap, CornerDownLeft } from 'lucide-react';
import type { GlobalOption, BreadcrumbItem, Catalog } from '../../data/optionsData';
type BulkItem = {
  id: string;
  name: string;
  price: string;
  description: string;
  isStandard: boolean;
  expanded: boolean;
  matchedOption: GlobalOption | null;
  suggestions: GlobalOption[];
  showSuggestions: boolean;
};
const emptyBulkItem = (): BulkItem => ({
  id: `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  name: '',
  price: '0',
  description: '',
  isStandard: false,
  expanded: false,
  matchedOption: null,
  suggestions: [],
  showSuggestions: false
});
export function CreateOptionModal({
  isOpen,
  onClose,
  existingOptions,
  onCreateNew,
  onLinkExisting,
  breadcrumbs,
  catalogs = []
}: {
  isOpen: boolean;
  onClose: () => void;
  existingOptions: GlobalOption[];
  onCreateNew: (option: GlobalOption) => void;
  onLinkExisting: (option: GlobalOption) => void;
  breadcrumbs: BreadcrumbItem[];
  catalogs?: Catalog[];
}) {
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  // --- Single mode state ---
  const [step, setStep] = useState<'search' | 'form'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExisting, setSelectedExisting] = useState<GlobalOption | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(breadcrumbs[0]?.value || '');
  const [subcategory, setSubcategory] = useState(breadcrumbs[1]?.value || '');
  const [variantSet, setVariantSet] = useState(breadcrumbs[2]?.value || '');
  const [price, setPrice] = useState('0');
  const [isStandard, setIsStandard] = useState(false);
  const [enabled, setEnabled] = useState(true);
  // --- Bulk mode state ---
  const [bulkItems, setBulkItems] = useState<BulkItem[]>([emptyBulkItem()]);
  const [bulkCatalog, setBulkCatalog] = useState(breadcrumbs[0]?.value || (catalogs.length > 0 ? catalogs[0].name : ''));
  const [bulkSubcategory, setBulkSubcategory] = useState(breadcrumbs[1]?.value || '');
  const [bulkVariantSet, setBulkVariantSet] = useState(breadcrumbs[2]?.value || '');
  const nameInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // --- Single mode logic (unchanged) ---
  const matches = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return existingOptions.filter(o => o.name.toLowerCase().includes(q) || o.description.toLowerCase().includes(q) || o.tags?.some(t => t.toLowerCase().includes(q))).slice(0, 6);
  }, [searchQuery, existingOptions]);
  const exactMatch = matches.find(m => m.name.toLowerCase() === searchQuery.toLowerCase());
  const existingSubcategories = useMemo(() => {
    const cat = mode === 'single' ? category : bulkCatalog;
    return Array.from(new Set(existingOptions.filter(o => o.category === cat).map(o => o.subcategory)));
  }, [existingOptions, category, bulkCatalog, mode]);
  const existingVariantSets = useMemo(() => {
    const cat = mode === 'single' ? category : bulkCatalog;
    const sub = mode === 'single' ? subcategory : bulkSubcategory;
    return Array.from(new Set(existingOptions.filter(o => o.category === cat && o.subcategory === sub).map(o => o.variantSet)));
  }, [existingOptions, category, subcategory, bulkCatalog, bulkSubcategory, mode]);
  if (!isOpen) return null;
  // --- Single mode handlers ---
  const handleSelectExisting = (option: GlobalOption) => {
    setSelectedExisting(option);
    setName(option.name);
    setDescription(option.description);
    setCategory(option.category);
    setSubcategory(option.subcategory);
    setVariantSet(option.variantSet);
    setPrice(option.basePrice.toString());
    setIsStandard(option.isStandard);
    setEnabled(option.enabled);
    setStep('form');
  };
  const handleCreateNew = () => {
    setSelectedExisting(null);
    setName(searchQuery);
    setDescription('');
    setCategory(breadcrumbs[0]?.value || (catalogs.length > 0 ? catalogs[0].name : ''));
    setSubcategory(breadcrumbs[1]?.value || '');
    setVariantSet(breadcrumbs[2]?.value || '');
    setPrice('0');
    setIsStandard(false);
    setEnabled(true);
    setStep('form');
  };
  const handleSave = () => {
    if (selectedExisting) {
      onLinkExisting(selectedExisting);
    } else {
      onCreateNew({
        id: `new-${Date.now()}`,
        name,
        description,
        category,
        subcategory,
        variantSet,
        basePrice: parseFloat(price) || 0,
        isStandard,
        enabled,
        hasImage: false,
        tags: []
      });
    }
    setStep('search');
    setSearchQuery('');
    setSelectedExisting(null);
    onClose();
  };
  // --- Bulk mode handlers ---
  const searchForMatch = (query: string): {
    matched: GlobalOption | null;
    suggestions: GlobalOption[];
  } => {
    if (!query.trim()) return {
      matched: null,
      suggestions: []
    };
    const q = query.toLowerCase();
    const exact = existingOptions.find(o => o.name.toLowerCase() === q);
    if (exact) return {
      matched: exact,
      suggestions: []
    };
    const partial = existingOptions.filter(o => o.name.toLowerCase().includes(q)).slice(0, 3);
    return {
      matched: null,
      suggestions: partial
    };
  };
  const updateBulkItem = (id: string, updates: Partial<BulkItem>) => {
    setBulkItems(prev => prev.map(item => item.id === id ? {
      ...item,
      ...updates
    } : item));
  };
  const handleBulkNameChange = (id: string, value: string) => {
    const {
      matched,
      suggestions
    } = searchForMatch(value);
    updateBulkItem(id, {
      name: value,
      matchedOption: matched,
      suggestions: matched ? [] : suggestions,
      showSuggestions: !matched && suggestions.length > 0 && value.trim().length > 0,
      price: matched ? matched.basePrice.toString() : bulkItems.find(i => i.id === id)?.price || '0',
      expanded: matched ? false : bulkItems.find(i => i.id === id)?.expanded || false
    });
  };
  const handleBulkLinkSuggestion = (itemId: string, option: GlobalOption) => {
    updateBulkItem(itemId, {
      name: option.name,
      matchedOption: option,
      suggestions: [],
      showSuggestions: false,
      price: option.basePrice.toString()
    });
  };
  const addNewRow = (afterId?: string): string => {
    const newItem = emptyBulkItem();
    setBulkItems(prev => {
      if (!afterId) return [...prev, newItem];
      const idx = prev.findIndex(i => i.id === afterId);
      const next = [...prev];
      next.splice(idx + 1, 0, newItem);
      return next;
    });
    setTimeout(() => nameInputRefs.current[newItem.id]?.focus(), 30);
    return newItem.id;
  };
  const removeRow = (id: string) => {
    setBulkItems(prev => {
      if (prev.length <= 1) return [emptyBulkItem()];
      const idx = prev.findIndex(i => i.id === id);
      const next = prev.filter(i => i.id !== id);
      const focusIdx = Math.max(0, idx - 1);
      setTimeout(() => nameInputRefs.current[next[focusIdx]?.id]?.focus(), 30);
      return next;
    });
  };
  const handleBulkKeyDown = (e: React.KeyboardEvent, item: BulkItem, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      updateBulkItem(item.id, {
        showSuggestions: false
      });
      addNewRow(item.id);
    }
    if (e.key === 'Tab' && !e.shiftKey && index === bulkItems.length - 1 && item.name.trim()) {
      e.preventDefault();
      updateBulkItem(item.id, {
        showSuggestions: false
      });
      addNewRow(item.id);
    }
    if (e.key === 'Backspace' && !item.name && bulkItems.length > 1) {
      e.preventDefault();
      removeRow(item.id);
    }
    // Arrow Down: expand detail panel (only for new items with a name)
    if (e.key === 'ArrowDown' && item.name.trim() && !item.matchedOption) {
      e.preventDefault();
      updateBulkItem(item.id, {
        expanded: true
      });
    }
    // Arrow Up: collapse detail panel
    if (e.key === 'ArrowUp' && item.expanded) {
      e.preventDefault();
      updateBulkItem(item.id, {
        expanded: false
      });
    }
  };
  const validBulkItems = bulkItems.filter(i => i.name.trim());
  const newCount = validBulkItems.filter(i => !i.matchedOption).length;
  const linkedCount = validBulkItems.filter(i => i.matchedOption).length;
  const handleBulkAddAll = () => {
    validBulkItems.forEach(item => {
      if (item.matchedOption) {
        onLinkExisting(item.matchedOption);
      } else {
        onCreateNew({
          id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          name: item.name.trim(),
          description: item.description.trim(),
          category: bulkCatalog,
          subcategory: bulkSubcategory,
          variantSet: bulkVariantSet,
          basePrice: parseFloat(item.price) || 0,
          isStandard: item.isStandard,
          enabled: true,
          hasImage: false,
          tags: []
        });
      }
    });
    setBulkItems([emptyBulkItem()]);
    onClose();
  };
  const handleModeSwitch = (newMode: 'single' | 'bulk') => {
    setMode(newMode);
    if (newMode === 'single') {
      setStep('search');
      setSearchQuery('');
      setSelectedExisting(null);
    } else {
      setBulkItems([emptyBulkItem()]);
      setBulkCatalog(breadcrumbs[0]?.value || (catalogs.length > 0 ? catalogs[0].name : ''));
      setBulkSubcategory(breadcrumbs[1]?.value || '');
      setBulkVariantSet(breadcrumbs[2]?.value || '');
    }
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-204">
      <div className={`bg-white rounded-lg shadow-xl w-full mx-4 max-h-[90vh] flex flex-col ${mode === 'bulk' ? 'max-w-2xl' : 'max-w-lg'}`} data-id="element-205">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex-shrink-0" data-id="element-206">
          <div className="flex items-center justify-between mb-4" data-id="element-207">
            <div data-id="element-208">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-209">
                {mode === 'single' ? step === 'search' ? 'Add Option' : selectedExisting ? 'Link Existing Option' : 'Create New Option' : 'Bulk Add Options'}
              </h2>
              <p className="text-sm text-slate-500" data-id="element-210">
                {mode === 'single' ? step === 'search' ? 'Search or create a new option' : selectedExisting ? 'This option will be linked to your catalog' : 'This will create a new option' : 'Quickly add multiple options at once'}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-id="element-211">
              <X className="h-5 w-5" data-id="element-212" />
            </button>
          </div>
          {/* Mode toggle */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 w-fit" data-id="element-213">
            <button onClick={() => handleModeSwitch('single')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${mode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-214">
              <Plus className="h-3.5 w-3.5" data-id="element-215" /> Single
            </button>
            <button onClick={() => handleModeSwitch('bulk')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${mode === 'bulk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-216">
              <Zap className="h-3.5 w-3.5" data-id="element-217" /> Bulk Add
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" data-id="element-218">
          {mode === 'single' /* ========== SINGLE MODE (unchanged) ========== */ ? <>
              {step === 'search' ? <div className="p-6 space-y-4" data-id="element-219">
                  <div data-id="element-220">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-221">
                      Option Name
                    </label>
                    <div className="relative" data-id="element-222">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-223" />
                      <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Start typing to search or create..." className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-primary-500 focus:border-primary-500 text-sm" autoFocus data-id="element-224" />
                    </div>
                  </div>
                  {searchQuery.trim() && <div className="space-y-2" data-id="element-225">
                      {matches.length > 0 && <div data-id="element-226">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2" data-id="element-227">
                            Existing Options
                          </p>
                          <div className="space-y-1.5" data-id="element-228">
                            {matches.map(option => <button key={option.id} onClick={() => handleSelectExisting(option)} className="w-full flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-primary-300 hover:bg-primary-50/30 transition-all text-left group" data-id="element-229">
                                <div className="h-10 w-10 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-230">
                                  {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-231" /> : <ImageOff className="h-4 w-4 text-slate-300" data-id="element-232" />}
                                </div>
                                <div className="flex-1 min-w-0" data-id="element-233">
                                  <div className="flex items-center gap-2" data-id="element-234">
                                    <span className="font-medium text-slate-900 text-sm group-hover:text-primary-700 truncate" data-id="element-235">
                                      {option.name}
                                    </span>
                                    {option.isStandard && <Star className="h-3 w-3 text-emerald-500 fill-emerald-500 flex-shrink-0" data-id="element-236" />}
                                  </div>
                                  <p className="text-xs text-slate-500 truncate" data-id="element-237">
                                    {option.category} › {option.subcategory} ›{' '}
                                    {option.variantSet}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0" data-id="element-238">
                                  {option.isStandard ? <span className="text-xs font-medium text-emerald-600" data-id="element-239">
                                      Included
                                    </span> : <span className="text-sm font-medium text-slate-900" data-id="element-240">
                                      ${option.basePrice.toLocaleString()}
                                    </span>}
                                </div>
                                <Link2 className="h-4 w-4 text-slate-300 group-hover:text-primary-500 flex-shrink-0" data-id="element-241" />
                              </button>)}
                          </div>
                        </div>}
                      {!exactMatch && <div data-id="element-242">
                          {matches.length > 0 && <div className="relative my-3" data-id="element-243">
                              <div className="absolute inset-0 flex items-center" data-id="element-244">
                                <div className="w-full border-t border-slate-200" data-id="element-245" />
                              </div>
                              <div className="relative flex justify-center" data-id="element-246">
                                <span className="bg-white px-3 text-xs text-slate-400" data-id="element-247">
                                  or
                                </span>
                              </div>
                            </div>}
                          <button onClick={handleCreateNew} className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-primary-400 hover:bg-primary-50/30 transition-all text-left group" data-id="element-248">
                            <div className="h-10 w-10 rounded-md bg-primary-50 border border-primary-200 flex items-center justify-center flex-shrink-0" data-id="element-249">
                              <Plus className="h-5 w-5 text-primary-600" data-id="element-250" />
                            </div>
                            <div className="flex-1" data-id="element-251">
                              <p className="font-medium text-slate-900 text-sm group-hover:text-primary-700" data-id="element-252">
                                Create "{searchQuery}"
                              </p>
                              <p className="text-xs text-slate-500" data-id="element-253">
                                New option — will be added to the catalog
                              </p>
                            </div>
                            <Sparkles className="h-4 w-4 text-slate-300 group-hover:text-primary-500 flex-shrink-0" data-id="element-254" />
                          </button>
                        </div>}
                    </div>}
                  {!searchQuery.trim() && <div className="text-center py-8" data-id="element-255">
                      <Search className="h-10 w-10 text-slate-200 mx-auto mb-3" data-id="element-256" />
                      <p className="text-sm text-slate-500" data-id="element-257">
                        Type an option name to search
                      </p>
                      <p className="text-xs text-slate-400 mt-1" data-id="element-258">
                        If it doesn't exist, you can create it
                      </p>
                    </div>}
                </div> : <div className="p-6 space-y-5" data-id="element-259">
                  {selectedExisting && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-id="element-260">
                      <Link2 className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-261" />
                      <div data-id="element-262">
                        <p className="text-sm font-medium text-blue-900" data-id="element-263">
                          Linking existing option
                        </p>
                        <p className="text-xs text-blue-700 mt-0.5" data-id="element-264">
                          This option already exists. You can customize it after
                          adding.
                        </p>
                      </div>
                    </div>}
                  {!selectedExisting && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-265">
                      <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" data-id="element-266" />
                      <div data-id="element-267">
                        <p className="text-sm font-medium text-amber-900" data-id="element-268">
                          Creating new option
                        </p>
                        <p className="text-xs text-amber-700 mt-0.5" data-id="element-269">
                          This will be added to the database and available to
                          all builders.
                        </p>
                      </div>
                    </div>}
                  <div data-id="element-270">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-271">
                      Option Name
                    </label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" readOnly={!!selectedExisting} data-id="element-272" />
                  </div>
                  <div data-id="element-273">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-274">
                      Description
                    </label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" readOnly={!!selectedExisting} data-id="element-275" />
                  </div>
                  <div className="grid grid-cols-3 gap-3" data-id="element-276">
                    <div data-id="element-277">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-278">
                        Catalog
                      </label>
                      {selectedExisting ? <input type="text" value={category} readOnly className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-sm text-slate-600" data-id="element-279" /> : catalogs.length > 0 ? <div className="relative" data-id="element-280">
                          <select value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm appearance-none bg-white pr-8" data-id="element-281">
                            <option value="" disabled data-id="element-282">
                              Select catalog
                            </option>
                            {catalogs.map(c => <option key={c.id} value={c.name} data-id="element-283">
                                {c.name}
                              </option>)}
                          </select>
                          <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-284" />
                        </div> : <div className="px-3 py-2 border border-dashed border-slate-300 rounded-md bg-slate-50 text-sm text-slate-400 flex items-center gap-1.5" data-id="element-285">
                          <Layers className="h-3.5 w-3.5" data-id="element-286" />
                          No catalogs
                        </div>}
                    </div>
                    <div data-id="element-287">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-288">
                        Subcategory
                      </label>
                      {selectedExisting ? <input type="text" value={subcategory} readOnly className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-sm text-slate-600" data-id="element-289" /> : <input type="text" value={subcategory} onChange={e => setSubcategory(e.target.value)} list="subcategory-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Kitchen" data-id="element-290" />}
                      <datalist id="subcategory-list" data-id="element-291">
                        {existingSubcategories.map(s => <option key={s} value={s} data-id="element-292" />)}
                      </datalist>
                    </div>
                    <div data-id="element-293">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-294">
                        Variant Set
                      </label>
                      {selectedExisting ? <input type="text" value={variantSet} readOnly className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-sm text-slate-600" data-id="element-295" /> : <input type="text" value={variantSet} onChange={e => setVariantSet(e.target.value)} list="variantset-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Countertops" data-id="element-296" />}
                      <datalist id="variantset-list" data-id="element-297">
                        {existingVariantSets.map(v => <option key={v} value={v} data-id="element-298" />)}
                      </datalist>
                    </div>
                  </div>
                  <div data-id="element-299">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-300">
                      Base Price
                    </label>
                    <div className="relative" data-id="element-301">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-302">
                        $
                      </span>
                      <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" readOnly={!!selectedExisting} data-id="element-303" />
                    </div>
                  </div>
                  {!selectedExisting && <>
                      <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50" data-id="element-304">
                        <div className="flex items-center justify-between" data-id="element-305">
                          <div data-id="element-306">
                            <label className="block text-sm font-medium text-slate-900" data-id="element-307">
                              Include as Standard
                            </label>
                            <p className="text-xs text-slate-500 mt-1" data-id="element-308">
                              {isStandard ? 'Included at no extra cost' : 'This is an upgrade option'}
                            </p>
                          </div>
                          <Toggle checked={isStandard} onChange={setIsStandard} label={isStandard ? 'Standard' : 'Upgrade'} data-id="element-309" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-310">
                        <div data-id="element-311">
                          <label className="block text-sm font-medium text-slate-700" data-id="element-312">
                            Availability
                          </label>
                          <p className="text-xs text-slate-500" data-id="element-313">
                            Make this option available for selection
                          </p>
                        </div>
                        <Toggle checked={enabled} onChange={setEnabled} label={enabled ? 'Enabled' : 'Disabled'} data-id="element-314" />
                      </div>
                    </>}
                </div>}
            </> /* ========== BULK MODE ========== */ : <div className="p-6 space-y-4" data-id="element-315">
              {/* Shared context */}
              <div className="grid grid-cols-3 gap-3" data-id="element-316">
                <div data-id="element-317">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-318">
                    Catalog
                  </label>
                  {catalogs.length > 0 ? <div className="relative" data-id="element-319">
                      <select value={bulkCatalog} onChange={e => setBulkCatalog(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm appearance-none bg-white pr-8" data-id="element-320">
                        <option value="" disabled data-id="element-321">
                          Select
                        </option>
                        {catalogs.map(c => <option key={c.id} value={c.name} data-id="element-322">
                            {c.name}
                          </option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-323" />
                    </div> : <div className="px-3 py-2 border border-dashed border-slate-300 rounded-md bg-slate-50 text-sm text-slate-400" data-id="element-324">
                      No catalogs
                    </div>}
                </div>
                <div data-id="element-325">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-326">
                    Subcategory
                  </label>
                  <input type="text" value={bulkSubcategory} onChange={e => setBulkSubcategory(e.target.value)} list="bulk-subcat-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Kitchen" data-id="element-327" />
                  <datalist id="bulk-subcat-list" data-id="element-328">
                    {existingSubcategories.map(s => <option key={s} value={s} data-id="element-329" />)}
                  </datalist>
                </div>
                <div data-id="element-330">
                  <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5" data-id="element-331">
                    Variant Set
                  </label>
                  <input type="text" value={bulkVariantSet} onChange={e => setBulkVariantSet(e.target.value)} list="bulk-vs-list" className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm" placeholder="e.g. Countertops" data-id="element-332" />
                  <datalist id="bulk-vs-list" data-id="element-333">
                    {existingVariantSets.map(v => <option key={v} value={v} data-id="element-334" />)}
                  </datalist>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4" data-id="element-335">
                {/* Column headers */}
                <div className="flex items-center gap-2 px-1 mb-2" data-id="element-336">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-1" data-id="element-337">
                    Option Name
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-16 text-center" data-id="element-338">
                    Status
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider w-20 text-right" data-id="element-339">
                    Price
                  </span>
                  <span className="w-7" data-id="element-340" />
                </div>

                {/* Repeater rows */}
                <div className="space-y-1" data-id="element-341">
                  {bulkItems.map((item, index) => <div key={item.id} data-id="element-342">
                      <div className="flex items-center gap-2 group relative" data-id="element-343">
                        {/* Row number */}
                        <span className="text-[10px] text-slate-300 w-4 text-right tabular-nums flex-shrink-0" data-id="element-344">
                          {index + 1}
                        </span>

                        {/* Name input */}
                        <div className="flex-1 relative" data-id="element-345">
                          <input ref={el => {
                      nameInputRefs.current[item.id] = el;
                    }} type="text" value={item.name} onChange={e => handleBulkNameChange(item.id, e.target.value)} onKeyDown={e => handleBulkKeyDown(e, item, index)} onFocus={() => {
                      if (item.suggestions.length > 0 && item.name.trim()) updateBulkItem(item.id, {
                        showSuggestions: true
                      });
                    }} onBlur={() => setTimeout(() => updateBulkItem(item.id, {
                      showSuggestions: false
                    }), 150)} placeholder={index === 0 ? 'Type option name...' : 'Next option...'} className={`w-full px-2.5 py-2 border rounded-md text-sm transition-colors focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none ${item.matchedOption ? 'border-teal-300 bg-teal-50/30' : item.name.trim() ? 'border-amber-300 bg-amber-50/20' : 'border-slate-200'}`} data-id="element-346" />
                          {/* Suggestions dropdown */}
                          {item.showSuggestions && item.suggestions.length > 0 && <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-md shadow-lg border border-slate-200 py-1 z-20" data-id="element-347">
                                {item.suggestions.map(opt => <button key={opt.id} onMouseDown={e => {
                        e.preventDefault();
                        handleBulkLinkSuggestion(item.id, opt);
                      }} className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-50 transition-colors" data-id="element-348">
                                    <Link2 className="h-3 w-3 text-teal-500 flex-shrink-0" data-id="element-349" />
                                    <span className="text-sm text-slate-700 truncate flex-1" data-id="element-350">
                                      {opt.name}
                                    </span>
                                    <span className="text-xs text-slate-400" data-id="element-351">
                                      ${opt.basePrice.toLocaleString()}
                                    </span>
                                  </button>)}
                              </div>}
                        </div>

                        {/* Status badge */}
                        <div className="w-16 flex justify-center flex-shrink-0" data-id="element-352">
                          {item.matchedOption ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-200" data-id="element-353">
                              <Link2 className="h-2.5 w-2.5" data-id="element-354" /> Linked
                            </span> : item.name.trim() ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200" data-id="element-355">
                              <Sparkles className="h-2.5 w-2.5" data-id="element-356" /> New
                            </span> : null}
                        </div>

                        {/* Price */}
                        <div className="w-20 flex-shrink-0" data-id="element-357">
                          <div className="relative" data-id="element-358">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs" data-id="element-359">
                              $
                            </span>
                            <input type="number" value={item.price} onChange={e => updateBulkItem(item.id, {
                        price: e.target.value
                      })} className={`w-full pl-5 pr-2 py-2 border rounded-md text-sm text-right ${item.matchedOption ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-200'}`} readOnly={!!item.matchedOption} tabIndex={-1} data-id="element-360" />
                          </div>
                        </div>

                        {/* Expand toggle — only for new items */}
                        {!item.matchedOption && item.name.trim() ? <button onClick={() => updateBulkItem(item.id, {
                    expanded: !item.expanded
                  })} className={`w-7 h-7 flex items-center justify-center rounded transition-all flex-shrink-0 ${item.expanded ? 'text-primary-500 bg-primary-50' : 'text-slate-300 opacity-0 group-hover:opacity-100 hover:text-slate-500 hover:bg-slate-100'}`} tabIndex={-1} title={item.expanded ? 'Collapse details' : 'Add details'} data-id="element-361">
                            {item.expanded ? <ChevronUp className="h-3.5 w-3.5" data-id="element-362" /> : <ChevronDown className="h-3.5 w-3.5" data-id="element-363" />}
                          </button> : <button onClick={() => removeRow(item.id)} className="w-7 h-7 flex items-center justify-center rounded text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0" tabIndex={-1} data-id="element-364">
                            <Trash2 className="h-3.5 w-3.5" data-id="element-365" />
                          </button>}

                        {/* Remove — show separately when expanded */}
                        {!item.matchedOption && item.name.trim() && <button onClick={() => removeRow(item.id)} className="w-7 h-7 flex items-center justify-center rounded text-slate-300 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all flex-shrink-0" tabIndex={-1} data-id="element-366">
                            <Trash2 className="h-3.5 w-3.5" data-id="element-367" />
                          </button>}
                      </div>

                      {/* Expanded detail panel */}
                      {item.expanded && !item.matchedOption && item.name.trim() && <div className="ml-5 mr-7 mt-1 mb-2 pl-4 border-l-2 border-amber-200 bg-amber-50/20 rounded-r-md p-3 space-y-3" data-id="element-368">
                            <div data-id="element-369">
                              <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1" data-id="element-370">
                                Description
                              </label>
                              <textarea value={item.description} onChange={e => updateBulkItem(item.id, {
                      description: e.target.value
                    })} rows={2} placeholder="Optional description..." className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" tabIndex={-1} data-id="element-371" />
                            </div>
                            <div className="flex items-center justify-between" data-id="element-372">
                              <div data-id="element-373">
                                <label className="block text-[11px] font-medium text-slate-500 uppercase tracking-wider" data-id="element-374">
                                  Standard
                                </label>
                                <p className="text-[10px] text-slate-400 mt-0.5" data-id="element-375">
                                  {item.isStandard ? 'Included at no extra cost' : 'Upgrade option'}
                                </p>
                              </div>
                              <Toggle checked={item.isStandard} onChange={val => updateBulkItem(item.id, {
                      isStandard: val,
                      price: val ? '0' : item.price
                    })} label={item.isStandard ? 'Standard' : 'Upgrade'} data-id="element-376" />
                            </div>
                          </div>}
                    </div>)}
                </div>

                {/* Hint */}
                <div className="flex items-center gap-1.5 mt-3 px-5" data-id="element-377">
                  <CornerDownLeft className="h-3 w-3 text-slate-300" data-id="element-378" />
                  <span className="text-[11px] text-slate-400" data-id="element-379">
                    <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-380">
                      Tab
                    </kbd>{' '}
                    /{' '}
                    <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-381">
                      Enter
                    </kbd>{' '}
                    add another ·{' '}
                    <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] font-mono" data-id="element-382">
                      ↓
                    </kbd>{' '}
                    expand details
                  </span>
                </div>
              </div>

              {/* Summary */}
              {validBulkItems.length > 0 && <div className="bg-slate-50 rounded-lg border border-slate-200 px-4 py-3 flex items-center justify-between" data-id="element-383">
                  <div className="flex items-center gap-3 text-xs" data-id="element-384">
                    {newCount > 0 && <span className="flex items-center gap-1 text-amber-700" data-id="element-385">
                        <Sparkles className="h-3 w-3" data-id="element-386" /> {newCount} new
                      </span>}
                    {linkedCount > 0 && <span className="flex items-center gap-1 text-teal-700" data-id="element-387">
                        <Link2 className="h-3 w-3" data-id="element-388" /> {linkedCount} linked
                      </span>}
                    <span className="text-slate-400" data-id="element-389">·</span>
                    <span className="text-slate-600 font-medium" data-id="element-390">
                      {validBulkItems.length} total
                    </span>
                  </div>
                </div>}
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-slate-200 flex-shrink-0" data-id="element-391">
          <div data-id="element-392">
            {mode === 'single' && step === 'form' && <button onClick={() => {
            setStep('search');
            setSelectedExisting(null);
          }} className="text-sm text-slate-500 hover:text-slate-700" data-id="element-393">
                ← Back to search
              </button>}
          </div>
          <div className="flex items-center gap-3" data-id="element-394">
            <Button variant="ghost" onClick={onClose} data-id="element-395">
              Cancel
            </Button>
            {mode === 'single' && step === 'form' && <Button onClick={handleSave} disabled={!name.trim() || !category.trim() || !subcategory.trim() || !variantSet.trim()} leftIcon={selectedExisting ? <Link2 className="h-4 w-4" data-id="element-397" /> : <Plus className="h-4 w-4" data-id="element-398" />} data-id="element-396">
                {selectedExisting ? 'Link Option' : 'Create Option'}
              </Button>}
            {mode === 'bulk' && <Button onClick={handleBulkAddAll} disabled={validBulkItems.length === 0 || !bulkCatalog.trim() || !bulkSubcategory.trim() || !bulkVariantSet.trim()} leftIcon={<Zap className="h-4 w-4" data-id="element-400" />} data-id="element-399">
                Add All ({validBulkItems.length})
              </Button>}
          </div>
        </div>
      </div>
    </div>;
}